import { useEffect, useState } from "react";

import { useAppointments } from "components/AppointmentsList/hooks/useAppointments";

/**
 * Auto-selects the closest upcoming (or most recent past) appointment for the
 * given doctor on the chosen date, and exposes the selected patient +
 * consultation.
 *
 * Pass `enabled: false` to suppress the underlying fetch entirely — used by
 * the home page when the user has no roster / staff member so we don't
 * silently pick up some other doctor's appointment.
 */
export const useScheduleAutoSelect = ({ doctorId, enabled = true }) => {
  const [scheduleDate, setScheduleDate] = useState(new Date());
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [hasAppointmentsForDay, setHasAppointmentsForDay] = useState(true);

  const { data: scheduleData } = useAppointments({
    is5minsAppointment: false,
    selectedStartDate: scheduleDate,
    selectedEndDate: scheduleDate,
    selectedDoctors: doctorId ? [doctorId.toString()] : [],
    enabled,
  });

  useEffect(() => {
    if (!enabled) {
      // No roster / no staff member — don't auto-pick anything. Without
      // this guard the appointments API returns the whole practice's
      // schedule (empty selectedDoctors == no filter) and we'd silently
      // surface another doctor's patient on the home page.
      setSelectedPatient(null);
      setSelectedAppointment(null);
      setHasAppointmentsForDay(false);
      return;
    }
    if (!scheduleData?.data) return;

    const now = new Date();
    const allSlots = scheduleData.data.flatMap((day) =>
      day.doctors.flatMap((doctor) => doctor.schedule)
    );
    const withPatient = allSlots.filter((a) => a.consultation?.patient?.id);

    setHasAppointmentsForDay(withPatient.length > 0);

    if (withPatient.length === 0) {
      setSelectedPatient(null);
      setSelectedAppointment(null);
      return;
    }

    const upcoming = withPatient.filter((a) => new Date(a.start) >= now);
    const past = withPatient.filter((a) => new Date(a.start) < now);

    // An appointment with an unparseable start fails both filters above, so
    // upcoming and past can both be empty even when withPatient is not —
    // guard the reduces or the empty-array reduce throws and white-screens.
    const closest =
      upcoming.length > 0
        ? upcoming.reduce((best, a) =>
            new Date(a.start) < new Date(best.start) ? a : best
          )
        : past.length > 0
        ? past.reduce((best, a) =>
            new Date(a.start) > new Date(best.start) ? a : best
          )
        : null;

    if (!closest) {
      setSelectedPatient(null);
      setSelectedAppointment(null);
      return;
    }

    setSelectedPatient(closest.consultation.patient);
    setSelectedAppointment(closest.consultation);
  }, [scheduleData, enabled]);

  const selectPatientManually = (patient, appointment) => {
    setSelectedPatient(patient);
    setSelectedAppointment(appointment);
  };

  return {
    scheduleDate,
    setScheduleDate,
    selectedPatient,
    selectedAppointment,
    hasAppointmentsForDay,
    selectPatientManually,
  };
};
